import services from '.'
import { IData, INewUser, IUser, IUserLogs } from '../@types'
import { getAxiosErrorMessage } from '../resources'

const baseUrl = '/users'

async function update(
  data: Partial<IUser & { newPassword: string; oldPassword: string }>,
): Promise<void> {
  try {
    const {
      email,
      level,
      name,
      surname,
      phoneNumber,
      newPassword,
      oldPassword,
      id,
    } = data

    await services.api.put(`${baseUrl}`, {
      id,
      email,
      name,
      level,
      surname,
      phoneNumber,
      newPassword,
      password: oldPassword,
    })
  } catch (error) {
    console.log(error)

    throw new Error(getAxiosErrorMessage(error))
  }
}

async function getProfileInfo(): Promise<IUser | undefined> {
  try {
    const user = await services.api
      .get<IData<IUser>>(baseUrl)
      .then((response) => {
        if (response && response.data) return response?.data?.data
        return {} as IUser
      })

    return user
  } catch (error) {
    throw new Error(getAxiosErrorMessage(error))
  }
}

async function getUserById(id: string): Promise<IUser | undefined> {
  try {
    const user = await services.api
      .get<IData<IUser>>(`${baseUrl}/${id}`)
      .then((response) => {
        if (response && response.data) return response?.data?.data
        return {} as IUser
      })

    return user
  } catch (error) {
    throw new Error(getAxiosErrorMessage(error))
  }
}

async function getAll(): Promise<IUser[] | undefined> {
  try {
    const users = await services.api
      .get<IData<IUser[]>>(baseUrl)
      .then((response) => {
        if (response && response.data) return response?.data?.data
      })

    return users
  } catch (error) {
    throw new Error(getAxiosErrorMessage(error))
  }
}

async function getCollaborators(): Promise<IUser[] | undefined> {
  try {
    const { data } = await services.api.get<IData<IUser[]>>(baseUrl)

    if (data?.data)
      return data.data.filter(({ accessLevel }) => accessLevel !== 'STUDENT')

    return []
  } catch (error) {
    throw new Error(getAxiosErrorMessage(error))
  }
}

async function getByType(type: 'USER' | 'ADMIN'): Promise<IUser[] | undefined> {
  try {
    const users = await services.api
      .get<IData<IUser[]>>(`${baseUrl}?type=${type}`)
      .then((response) => {
        if (response && response.data) return response?.data?.data
      })

    const sorted = users?.sort((a, b) => a.createdAt.localeCompare(b.createdAt))

    return sorted
  } catch (error) {
    throw new Error(getAxiosErrorMessage(error))
  }
}

async function getLogs(): Promise<IUserLogs | undefined> {
  try {
    const userLogs = await services.api
      .get<IData<IUserLogs>>(`${baseUrl}/logs`)
      .then((response) => {
        return response?.data?.data
      })

    return userLogs
  } catch (error) {
    throw new Error(getAxiosErrorMessage(error))
  }
}

async function create({
  accessLevel,
  email,
  name,
  password,
  surname,
}: INewUser): Promise<void> {
  try {
    if (accessLevel !== 'STUDENT')
      await services.api.post<IData<Array<IUser>>>(`${baseUrl}/admin`, {
        accessLevel,
        email,
        name,
        password,
        surname,
      })
    else
      await services.api.post<IData<Array<IUser>>>(`${baseUrl}`, {
        accessLevel,
        email,
        name,
        password,
        surname,
      })
  } catch (error) {
    throw new Error(getAxiosErrorMessage(error))
  }
}

async function toggleActive({
  id,
  isDisabled,
}: Pick<IUser, 'id' | 'isDisabled'>): Promise<void> {
  try {
    await services.api.put(baseUrl, { id, isDisabled })
  } catch (error) {
    throw new Error(getAxiosErrorMessage(error))
  }
}

export async function deleteById(id: string): Promise<void> {
  try {
    await services.api.delete(`${baseUrl}/${id}`)
  } catch (error) {
    throw new Error(getAxiosErrorMessage(error))
  }
}

export default {
  update,
  getProfileInfo,
  create,
  getLogs,
  getAll,
  getByType,
  toggleActive,
  deleteById,
  getUserById,
  getCollaborators,
}
